import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Calendar, ChevronLeft } from "lucide-react";
import { companyInfo } from "../data";
import { fetchServiceBySlug, fetchGalleryBySlug } from "../lib/cms";
import Lightbox from "../components/Lightbox";

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const serviceData = await fetchServiceBySlug(slug);
        if (!serviceData) {
          // Hvis ydelsen ikke findes, gå tilbage til ydelser
          navigate("/service-og-reparation");
          return;
        }
        setService(serviceData);

        // Hent tilknyttet galleri, hvis der er et
        if (serviceData.gallery_slug) {
          const galleryData = await fetchGalleryBySlug(serviceData.gallery_slug);
          setGallery(galleryData);
        } else {
          setGallery(null);
        }
      } catch (error) {
        console.error("Fejl ved indlæsning af ydelsesdetaljer:", error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [slug, navigate]);

  useEffect(() => {
    if (!service) return;

    let title = `${service.title} | Brdr. Lorenzen ApS`;
    let description = service.meta_description || `Læs mere om ${service.title} hos Brdr. Lorenzen ApS.`;
    let ogImgUrl = "";

    if (slug === "klargoring-til-syn") {
      title = "Klargøring til syn | Synstjek inden synskontrol";
      description = "Kom ind til os og få din bil klargjort til syn. Vi gennemgår hele din bil og kører den til synshallen for dig – så bliver det ikke nemmere. Klik her.";
    } else if (slug === "lanebil") {
      title = "Gratis lånebil ved reparation hos Brdr. Lorenzen";
      description = "Hos os får du en gratis lånebil, så din hverdag ikke bliver besværet, mens vi reparer din bil. Læs mere om vores arbejde og gratis lånebiler her.";
    } else if (slug === "daekopbevaring") {
      title = "Dækopbevaring | Vi opbevarer dine dæk sikkert";
      description = "Brug dækopbevaring, når du vil være sikker på, at dine dæk opbevares optimalt og væk fra vind og vejr. Læs meget mere og bestil tid her.";
    } else if (slug === "olieskift-skylning-af-automatgearkasse") {
      title = "Olieskift/skylning af automatgearkasse | Brdr. Lorenzen ApS";
      description = "Forlæng levetiden og omkostningerne til service og reparation af din automatgearkasse. Læs mere her.";
    } else if (slug === "pladevaerksted") {
      title = "Pladeværksted | Professionel udbedring af autoskader";
      description = "På vores pladeværksted tager vi os af alle former for autoskader, og vi samarbejder med alle forsikringsselskaber. Læs mere og book tid her.";
    } else if (slug === "serviceeftersyn") {
      title = "Bilservice | Få et grundigt serviceeftersyn på din bil";
      description = "Der er mange fordele ved at bruge os som dit faste værksted til serviceeftersyn. Læs meget mere om dem her, og bestil tid til næste bilservice online.";
    } else if (slug === "fejlsogning") {
      title = "Fejlsøgning på din bils elektroniske komponenter";
      description = "Lad os foretage fejlsøgningen på din bil. Når vi har fundet frem til fejlen, får du et uforpligtende tilbud på eine reparation. Læs mere her.";
    } else if (slug === "vinterdaek") {
      title = "Skift til vinterdæk | Professionelt dækskifte";
      description = "Er det tid til, at du får skiftet til vinterdæk?Så kom ind til os og lad os ordne det for dig. I øvrigt opbevarer vi også dine sommerdæk. Læs med her.";
    } else if (slug === "sommerdaek") {
      title = "Lad os skifte til sommerdæk for dig";
      description = "Kom ind til os og lad os skifte til sommerdæk for dig, så du er sikker på vejene hele sommerhalvåret. Læs mere og bestil tid her.";
    }

    document.title = title;

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', description);

    let ogImage = document.querySelector('meta[property="og:image"]');
    if (!ogImage) {
      ogImage = document.createElement('meta');
      ogImage.setAttribute('property', 'og:image');
      document.head.appendChild(ogImage);
    }
    ogImage.setAttribute('content', ogImgUrl);
  }, [service, slug]);

  if (loading) {
    return (
      <div className="bg-slate-50 text-slate-900 min-h-screen flex items-center justify-center">
        <div className="space-y-4 text-center">
          <div className="h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-slate-600 text-sm">Henter oplysninger...</p>
        </div>
      </div>
    );
  }

  if (!service) return null;

  return (
    <div className="bg-slate-50 text-slate-900 antialiased min-h-screen">
      {/* Header Banner */}
      <section className="relative overflow-hidden bg-slate-900 py-24 border-b border-slate-800">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://assets.auxodev.dk/assets/845935e5-3993-406d-896b-e0a4423b6917/088d09ec-24bf-4ddd-8ad9-c0c669c0f1fd.jpg"
            alt={service.title}
            className="h-full w-full object-cover filter brightness-30 contrast-105"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-transparent" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-left">
          <Link
            to="/service-og-reparation"
            className="inline-flex items-center gap-2 text-xs font-bold text-blue-400 hover:text-blue-300 transition-colors mb-4"
          >
            <ChevronLeft className="h-4 w-4" />
            Tilbage til ydelser
          </Link>
          <h1 className="text-2xl font-extrabold tracking-tight sm:text-4xl md:text-5xl text-white">
            {service.title}
          </h1>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            {/* Main Content (Verbatim from Scrape) */}
            <div className="lg:col-span-8 text-left space-y-12">
              {/* Verbatim Content */}
              <div
                className="prose prose-slate max-w-none text-slate-600 leading-relaxed space-y-6"
                dangerouslySetInnerHTML={{ __html: service.content }}
              />
            </div>

            {/* Sidebar Column */}
            <div className="lg:col-span-4 space-y-8">
              {/* Quick Contact Card */}
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 md:p-8 text-left space-y-6 shadow-lg">
                <h3 className="text-base font-bold text-slate-900 border-b border-slate-200 pb-3">
                  Book Tid til {service.title}
                </h3>

                <ul className="space-y-4 text-sm">
                  <li className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 shrink-0 text-blue-500" />
                    <div>
                      <p className="text-slate-900 font-semibold">{companyInfo.name}</p>
                      <p className="text-slate-600 mt-0.5">{companyInfo.address}</p>
                      <p className="text-slate-600">{companyInfo.zipCity}</p>
                    </div>
                  </li>
                  <li className="flex items-center gap-3">
                    <Phone className="h-5 w-5 shrink-0 text-blue-500" />
                    <a
                      href={`tel:${companyInfo.phone}`}
                      className="text-slate-900 hover:text-blue-600 transition-colors font-mono font-semibold"
                    >
                      {companyInfo.phoneFormatted}
                    </a>
                  </li>
                  <li className="flex items-center gap-3">
                    <Mail className="h-5 w-5 shrink-0 text-blue-500" />
                    <a
                      href={`mailto:${companyInfo.email}`}
                      className="text-slate-900 hover:text-blue-600 transition-colors font-semibold"
                    >
                      {companyInfo.email}
                    </a>
                  </li>
                  <li className="flex items-start gap-3 pt-4 border-t border-slate-200">
                    <Clock className="h-5 w-5 shrink-0 text-blue-500" />
                    <div className="space-y-1.5 w-full">
                      {companyInfo.openingHours.map((item) => (
                        <div key={item.days} className="flex justify-between text-xs">
                          <span className="text-slate-600">{item.days}</span>
                          <span className="text-slate-900 font-medium font-mono">{item.hours}</span>
                        </div>
                      ))}
                    </div>
                  </li>
                </ul>

                <div className="pt-4 border-t border-slate-200 space-y-3">
                  <Link
                    to="/online-booking"
                    state={{ preselectedService: service.title }}
                    className="flex w-full items-center justify-center gap-2 rounded-md bg-blue-600 py-3 text-sm font-bold text-white hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/10"
                  >
                    <Calendar className="h-4 w-4" />
                    Book Tid Online
                  </Link>
                  <a
                    href={`tel:${companyInfo.phone}`}
                    className="flex w-full items-center justify-center gap-2 rounded-md border border-slate-200 bg-white py-3 text-sm font-bold text-slate-900 hover:bg-slate-50 transition-colors shadow-sm"
                  >
                    <Phone className="h-4 w-4 text-blue-500" />
                    Ring Op Nu
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Integration */}
      {gallery && gallery.images && lightboxIndex !== null && (
        <Lightbox
          images={gallery.images}
          activeIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={(index) => setLightboxIndex(index)}
        />
      )}
    </div>
  );
}
